// useProctoring.js
import { useState, useEffect, useRef, useCallback } from "react";
import { calibrateGaze } from "../Utility/Calibrategaze";

/**
 * useProctoring — owns ALL proctoring infrastructure
 *
 * Responsibilities:
 *   1. Stream acquisition (camera + mic via single getUserMedia)
 *   2. Video pipeline (hidden <video> element for frame access)
 *   3. Audio pipeline (AudioContext + AnalyserNode for future VAD)
 *   4. MediaRecorder (chunk recording for backend upload)
 *   5. Gaze calibration (MediaPipe FaceMesh baseline)
 *   6. Violation logging
 *
 * Architecture:
 *   MediaStream (single getUserMedia call)
 *       ├── Video track → hidden <video> element (videoRef)
 *       │     ├── calibrateGaze() — one-time baseline during check-in
 *       │     └── future: frame loop → MediaPipe / COCO-SSD
 *       ├── Audio track → AudioContext + AnalyserNode (analyserRef)
 *       │     └── future: audio loop → VAD / volume spike detection
 *       └── MediaRecorder (recorderRef)
 *             └── future: chunk upload to S3 / backend
 *
 * Status lifecycle:
 *   "requesting" → "active"
 *                → "denied"  (user blocked permission)
 *                → "error"   (device not found, etc.)
 */

const CALIBRATION_DURATION_MS = 5000;

export default function useProctoring() {
  // ── Stream status ──
  const [status, setStatus] = useState("requesting");
  // "requesting" | "active" | "denied" | "error"

  // ── Calibration state ──
  const [calibrationState, setCalibrationState] = useState("idle");
  // "idle" | "running" | "done" | "failed"
  const [calibrationProgress, setCalibrationProgress] = useState(0); // 0–100
  const [calibrationError, setCalibrationError] = useState(null);
  const calibrationDataRef = useRef(null);

  // ── Violations ──
  const [violations, setViolations] = useState([]);

  // ── Refs ──
  const streamRef = useRef(null);
  const videoRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const videoLoopRef = useRef(null);
  const audioLoopRef = useRef(null);

  // ═══════════════════════════════════════════════════════════════════════════
  // VIOLATIONS
  // ═══════════════════════════════════════════════════════════════════════════

  const recordViolation = useCallback((reason, meta = {}) => {
    const entry = {
      reason,
      timestamp: new Date().toISOString(),
      ...meta,
    };
    setViolations((prev) => [...prev, entry]);
    console.log("[Proctoring] Violation:", entry);
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // STREAM ACQUISITION
  // ═══════════════════════════════════════════════════════════════════════════

  const acquireStream = useCallback(async () => {
    setStatus("requesting");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          frameRate: { ideal: 15 },
        },
        audio: true,
      });

      streamRef.current = stream;

      // Attach to video element if already mounted
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      }

      // ── Audio pipeline ──
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      // Read-only — NOT connected to destination

      audioCtxRef.current = audioCtx;
      analyserRef.current = analyser;

      // ── MediaRecorder (not recording yet) ──
      const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
        ? "video/webm;codecs=vp9"
        : "video/webm";

      const recorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: 500_000,
      });

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
          // Future: uploadChunk(e.data, testId, chunkIndex)
        }
      };

      recorderRef.current = recorder;
      setStatus("active");
      return true;
    } catch (err) {
      console.error("[Proctoring] getUserMedia failed:", err);
      if (
        err.name === "NotAllowedError" ||
        err.name === "PermissionDeniedError"
      ) {
        setStatus("denied");
      } else {
        setStatus("error");
      }
      return false;
    }
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // CALIBRATION
  // ═══════════════════════════════════════════════════════════════════════════

  const startCalibration = useCallback(async () => {
    if (calibrationState === "running") return;
    if (!videoRef.current || status !== "active") return;

    setCalibrationState("running");
    setCalibrationProgress(0);
    setCalibrationError(null);
    calibrationDataRef.current = null;

    const result = await calibrateGaze(videoRef.current, {
      durationMs: CALIBRATION_DURATION_MS,
      onProgress: ({ elapsed, total }) => {
        setCalibrationProgress(Math.min(100, Math.round((elapsed / total) * 100)));
      },
    });

    if (result.ok) {
      calibrationDataRef.current = result;
      setCalibrationState("done");
      setCalibrationProgress(100);
      console.log("[Proctoring] Calibration complete:", result);
    } else {
      const messages = {
        no_face_detected: "No face detected. Make sure you're visible in the camera.",
        too_few_frames: "Calibration incomplete. Please try again in better lighting.",
        mediapipe_load_failed: "Could not load face-tracking library. Check your connection.",
      };
      setCalibrationError(
        messages[result.reason] ?? "Calibration failed. Please try again."
      );
      setCalibrationState("failed");
    }
  }, [calibrationState, status]);

  // ═══════════════════════════════════════════════════════════════════════════
  // RECORDING
  // ═══════════════════════════════════════════════════════════════════════════

  const startRecording = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === "recording") return;
    chunksRef.current = [];
    recorder.start(30_000);
    console.log("[Proctoring] Recording started");
  }, []);

  const stopRecording = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== "recording") return;
    recorder.stop();
    console.log("[Proctoring] Recording stopped");
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // SNAPSHOT
  // ═══════════════════════════════════════════════════════════════════════════

  const captureSnapshot = useCallback(() => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) return null;

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0);

    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.6);
    });
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // CLEANUP
  // ═══════════════════════════════════════════════════════════════════════════

  const cleanup = useCallback(() => {
    if (videoLoopRef.current) {
      cancelAnimationFrame(videoLoopRef.current);
      videoLoopRef.current = null;
    }
    if (audioLoopRef.current) {
      clearInterval(audioLoopRef.current);
      audioLoopRef.current = null;
    }

    if (recorderRef.current?.state === "recording") {
      recorderRef.current.stop();
    }
    recorderRef.current = null;

    if (audioCtxRef.current?.state !== "closed") {
      audioCtxRef.current?.close();
    }
    audioCtxRef.current = null;
    analyserRef.current = null;

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    chunksRef.current = [];
  }, []);

  const retry = useCallback(() => {
    cleanup();
    acquireStream();
  }, [cleanup, acquireStream]);

  // ═══════════════════════════════════════════════════════════════════════════
  // LIFECYCLE
  // ═══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    acquireStream();
    return cleanup;
  }, [acquireStream, cleanup]);

  // ═══════════════════════════════════════════════════════════════════════════
  // PUBLIC API
  // ═══════════════════════════════════════════════════════════════════════════

  return {
    // Stream
    status, // "requesting" | "active" | "denied" | "error"
    videoRef, // attach to <video> element
    retry,

    // Calibration
    calibrationState, // "idle" | "running" | "done" | "failed"
    calibrationProgress, // 0–100
    calibrationError, // string | null
    calibrationData: calibrationDataRef.current,
    startCalibration,

    // Recording
    startRecording,
    stopRecording,
    captureSnapshot,

    // Violations
    violations,
    recordViolation,

    // Lifecycle
    cleanup,

    // Internals for future analysis hooks
    _streamRef: streamRef,
    _analyserRef: analyserRef,
    _videoLoopRef: videoLoopRef,
    _audioLoopRef: audioLoopRef,
  };
}